import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Users, Sparkles, Lock, Loader2, RefreshCw, X } from "lucide-react";

/**
 * Host-side "AI Find My Photos" toggle card. Available on Grand tier
 * out-of-the-box OR on any tier that buys the `face_grouping` add-on.
 *
 * Rendered inside EventManagePage. Polls /faces/status while a backfill
 * is running so the host sees progress in real time (indexed / total).
 */
const FaceGroupingCard = ({ event }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Add-on paywall state — mirrors GuestListPaywall so the two cards
  // behave identically for Petite / Classic hosts.
  const [addon, setAddon] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [paystack, setPaystack] = useState(null);
  const [buying, setBuying] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/faces/status`);
      setStatus(r.data);
    } catch (e) {
      console.debug("[faces] status skipped:", e?.message);
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!status?.backfill_running) return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [status?.backfill_running, load]);

  // Fetch add-on price + Paystack support only when the paywall is
  // actually going to render (tier lock, no Grand, no addon yet).
  const shouldFetchAddon = status && !status.tier_ok && !status.enabled;
  useEffect(() => {
    if (!shouldFetchAddon) return;
    (async () => {
      try {
        const [a, ps] = await Promise.all([
          api.get(`/payments/addons?currency=${currency}`),
          api.get(`/paystack/config`).catch(() => ({ data: { enabled: false, supported_currencies: [] } })),
        ]);
        setAddon(a.data.face_grouping);
        setPaystack(ps.data);
      } catch (e) { console.debug("[faces] addon fetch skipped:", e?.message); }
    })();
  }, [shouldFetchAddon, currency]);

  const usePaystack = paystack?.enabled && (paystack?.supported_currencies || []).includes(currency);
  const currencies = ["USD", ...(paystack?.supported_currencies || [])];

  const buyAddon = async () => {
    setBuying(true); setErr("");
    try {
      if (usePaystack) {
        const r = await api.post("/paystack/checkout", {
          purchase_type: "addon",
          event_id: event.id,
          addon: "face_grouping",
          currency,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.authorization_url;
      } else {
        const r = await api.post("/payments/addons/checkout", {
          event_id: event.id,
          addon: "face_grouping",
          currency,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.url;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBuying(false);
    }
  };

  const enable = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/events/${event.id}/faces/enable`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not enable face grouping.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Turn off AI face grouping and wipe all face data for this event?")) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/events/${event.id}/faces/disable`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not disable.");
    } finally { setBusy(false); }
  };

  const reindex = async () => {
    if (!window.confirm("Re-index every photo? This will clear existing face data and rebuild it.")) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/events/${event.id}/faces/reindex`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reindex failed.");
    } finally { setBusy(false); }
  };

  const tierOk = status?.tier_ok !== false;
  const guestLink = `${window.location.origin}/g/${event.slug}/find-me`;

  return (
    <div data-testid="face-grouping-card" className="bg-white rounded-3xl soft-shadow p-6">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-coral-300" />
        <h3 className="font-serif italic text-navy-700 text-2xl">AI Find My Photos</h3>
        <span className="text-[10px] uppercase tracking-wider bg-coral-100 text-coral-500 rounded-full px-2 py-0.5 font-semibold">Add-on</span>
      </div>
      <p className="text-sm text-navy-700/60 mt-1">
        Guests scan the QR, snap a selfie, and instantly see every photo they appear in — no scrolling, no login.
      </p>

      {!tierOk ? (
        <div data-testid="face-paywall" className="mt-5 rounded-2xl border border-cream-200 bg-cream-100 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <Lock className="w-4 h-4 text-navy-700/60" />
            <span className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Not yet unlocked</span>
          </div>
          <ul className="mt-3 text-sm text-navy-700/70 space-y-1.5">
            <li>· One-tap selfie find — no swiping through hundreds of photos</li>
            <li>· Faces are processed on our servers, no third-party AI</li>
            <li>· Consent screen for every guest (GDPR-safe)</li>
            <li>· Included free on Grand tier</li>
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="text-2xl font-serif text-navy-700">
              {addon
                ? `${addon.symbol || ""}${(addon.price ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : "—"}
              <span className="text-xs text-navy-700/50 ml-1">/ event</span>
            </div>
            {currencies.length > 1 && (
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="face-addon-currency"
                className="px-2.5 py-1.5 rounded-lg border border-cream-200 text-xs bg-white"
              >
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
          {err && <div className="mt-3 bg-red-50 text-red-700 rounded-md px-3 py-2 text-sm">{err}</div>}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={buyAddon}
              disabled={buying || !addon}
              data-testid="face-addon-buy"
              className="inline-flex items-center gap-1.5 bg-coral-300 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-coral-400 disabled:opacity-60"
            >
              {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Unlock for this event
            </button>
            <a href={`/dashboard/events/${event.id}/upgrade`}
               data-testid="face-upgrade-cta"
               className="inline-flex items-center gap-1.5 text-navy-700 border border-navy-700/20 px-4 py-2 rounded-full text-sm font-semibold hover:bg-cream-100">
              or upgrade to Grand (all features)
            </a>
          </div>
        </div>
      ) : !status?.enabled ? (
        <div className="mt-5">
          <ul className="text-sm text-navy-700/70 space-y-1.5">
            <li>· One-tap selfie find — no swiping through 500 photos</li>
            <li>· Faces are processed on our servers, no third-party AI</li>
            <li>· Guests must consent before their selfie is taken</li>
          </ul>
          {err && <div className="mt-3 bg-red-50 text-red-700 rounded-md px-3 py-2 text-sm">{err}</div>}
          <button onClick={enable} disabled={busy}
            data-testid="face-enable"
            className="mt-5 inline-flex items-center gap-2 bg-navy-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-navy-800 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Enable face grouping
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="rounded-2xl bg-cream-100 border border-cream-200 p-4">
            <div className="flex items-center justify-between text-xs uppercase tracking-wider font-semibold">
              <span className="text-navy-700/60">Indexing</span>
              <span className="text-navy-700">{status.indexed} / {status.total} photos</span>
            </div>
            <div className="mt-2 h-2 bg-white rounded-full overflow-hidden">
              <div
                className="h-full bg-coral-300 transition-all"
                style={{ width: `${status.total ? (status.indexed / status.total * 100) : 0}%` }}
                data-testid="face-progress-bar"
              />
            </div>
            <div className="mt-2 text-xs text-navy-700/60">
              {status.backfill_running ? "Working on it… this can take a minute or two." :
                status.ready ? "Ready — share the link below with your guests." :
                  "Waiting to start."}
            </div>
          </div>

          <div className="rounded-2xl border border-coral-200 bg-coral-50 p-4">
            <div className="text-xs uppercase tracking-wider text-coral-500 font-semibold">Guest find-me link</div>
            <div className="mt-1 flex flex-wrap gap-2 items-center">
              <input readOnly value={guestLink}
                data-testid="face-guest-link"
                className="flex-1 min-w-[220px] px-3 py-2 rounded-lg bg-white border border-cream-200 text-xs text-navy-700" />
              <button
                onClick={() => { navigator.clipboard.writeText(guestLink); }}
                data-testid="face-copy-link"
                className="bg-coral-300 text-white px-3 py-2 rounded-full text-xs font-semibold hover:bg-coral-400">
                Copy link
              </button>
              <a href={guestLink} target="_blank" rel="noreferrer"
                className="text-navy-700/70 hover:text-navy-700 text-xs underline">
                Open
              </a>
            </div>
          </div>

          {err && <div className="bg-red-50 text-red-700 rounded-md px-3 py-2 text-sm">{err}</div>}

          <div className="flex flex-wrap gap-2">
            <button onClick={reindex} disabled={busy}
              data-testid="face-reindex"
              className="inline-flex items-center gap-1.5 bg-white border border-navy-700/10 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100 disabled:opacity-60">
              <RefreshCw className="w-3.5 h-3.5" /> Re-index all photos
            </button>
            <button onClick={disable} disabled={busy}
              data-testid="face-disable"
              className="inline-flex items-center gap-1.5 text-red-500 border border-red-200 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-red-50 disabled:opacity-60">
              <X className="w-3.5 h-3.5" /> Turn off + wipe face data
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaceGroupingCard;
